/**
 * WordPress dependencies
 */
import {
	NumberControl,
	ToolsPanel,
	ToolsPanelItem,
} from '@safe-wordpress/components';
import { _x } from '@safe-wordpress/i18n';
import { useMemo } from '@safe-wordpress/element';

/**
 * Internal dependencies
 */
import {
	FontAppearanceControl,
	FontFamilyControl,
	FontSizePicker,
	LetterSpacingControl,
	LineHeightControl,
	TextAlignmentControl,
	TextDecorationControl,
	TextTransformControl,
	WhiteSpaceControl,
	WordBreakControl,
	WritingModeControl,
	getMergedFontFamiliesAndFontFamilyFaces,
	getEditorSettings,
	type FullEditorSettings,
} from './internal';

import {
	getInheritedValue,
	getPreviewValue,
	hasInheritedValue,
	hasUserValue,
} from '../../../../utils/props';
import type { ToolsPanelProps } from './types';

const MIN_TEXT_COLUMNS = 1;
const MAX_TEXT_COLUMNS = 6;

export function useHasTypographyPanel( settings: FullEditorSettings ): boolean {
	const hasFontFamily = useHasFontFamilyControl( settings );
	const hasLineHeight = useHasLineHeightControl( settings );
	const hasFontAppearance = useHasAppearanceControl( settings );
	const hasLetterSpacing = useHasLetterSpacingControl( settings );
	const hasTextAlign = useHasTextAlignmentControl( settings );
	const hasTextTransform = useHasTextTransformControl( settings );
	const hasTextDecoration = useHasTextDecorationControl( settings );
	const hasWritingMode = useHasWritingModeControl( settings );
	const hasTextColumns = useHasTextColumnsControl( settings );
	const hasFontSize = useHasFontSizeControl( settings );
	const hasWhiteSpaceControl = useHasWhiteSpaceControl( settings );
	const hasWordBreakControl = useHasWordBreakControl( settings );

	return (
		hasFontFamily ||
		hasLineHeight ||
		hasFontAppearance ||
		hasLetterSpacing ||
		hasTextAlign ||
		hasTextTransform ||
		hasFontSize ||
		hasTextDecoration ||
		hasWritingMode ||
		hasTextColumns ||
		hasWhiteSpaceControl ||
		hasWordBreakControl
	);
}

function useHasFontSizeControl( settings?: FullEditorSettings ): boolean {
	return (
		( settings?.typography?.defaultFontSizes !== false &&
			!! settings?.typography?.fontSizes?.default?.length ) ||
		!! settings?.typography?.fontSizes?.theme?.length ||
		!! settings?.typography?.fontSizes?.custom?.length ||
		!! settings?.typography?.customFontSize
	);
}

function useHasFontFamilyControl( settings?: FullEditorSettings ): boolean {
	return (
		[ 'default', 'theme', 'custom' ] as Array<
			keyof FullEditorSettings[ 'typography' ][ 'fontFamilies' ]
		>
	 ).some( ( key ) => settings?.typography?.fontFamilies?.[ key ]?.length );
}

function useHasLineHeightControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.lineHeight;
}

function useHasAppearanceControl( settings?: FullEditorSettings ): boolean {
	return (
		!! settings?.typography?.fontStyle ||
		!! settings?.typography?.fontWeight
	);
}

function useAppearanceControlLabel( settings?: FullEditorSettings ): string {
	if ( ! settings?.typography?.fontStyle ) {
		return _x( 'Font weight', 'text', 'nelio-ab-testing' );
	}
	if ( ! settings?.typography?.fontWeight ) {
		return _x( 'Font style', 'text', 'nelio-ab-testing' );
	}
	return _x( 'Appearance', 'text', 'nelio-ab-testing' );
}

function useHasLetterSpacingControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.letterSpacing;
}

function useHasTextTransformControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.textTransform;
}

function useHasTextAlignmentControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.textAlign;
}

function useHasTextDecorationControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.textDecoration;
}

function useHasWritingModeControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.writingMode;
}

function useHasTextColumnsControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.textColumns;
}

function useHasWhiteSpaceControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.whiteSpace;
}

function useHasWordBreakControl( settings?: FullEditorSettings ): boolean {
	return !! settings?.typography?.wordBreak;
}

/**
 * Concatenate all the font sizes into a single list for the font size picker.
 *
 * @param {Object} settings The global styles settings.
 *
 * @return {Array} The merged font sizes.
 */
function getMergedFontSizes( settings?: FullEditorSettings ) {
	const fontSizes = settings?.typography?.fontSizes;
	const defaultFontSizesEnabled = !! settings?.typography?.defaultFontSizes;
	return [
		...( fontSizes?.custom ?? [] ),
		...( fontSizes?.theme ?? [] ),
		...( defaultFontSizesEnabled ? fontSizes?.default ?? [] : [] ),
	];
}

export function TypographyPanel( {
	value: props,
	onChange,
}: ToolsPanelProps ): JSX.Element {
	const settings = getEditorSettings();

	// Font Family
	const hasFontFamilyEnabled = useHasFontFamilyControl( settings );
	const inheritedFontFamily = getInheritedValue( props.fontFamily );
	const { fontFamilies, fontFamilyFaces } = useMemo( () => {
		return getMergedFontFamiliesAndFontFamilyFaces(
			settings,
			inheritedFontFamily
		);
	}, [ settings, inheritedFontFamily ] );

	// Font Size
	const hasFontSizeEnabled = useHasFontSizeControl( settings );
	const disableCustomFontSizes = ! settings?.typography?.customFontSize;
	const mergedFontSizes = getMergedFontSizes( settings );

	// Appearance
	const hasAppearanceControl = useHasAppearanceControl( settings );
	const appearanceControlLabel = useAppearanceControlLabel( settings );
	const hasFontStyles = settings?.typography?.fontStyle;
	const hasFontWeights = settings?.typography?.fontWeight;

	// Line Height
	const hasLineHeightEnabled = useHasLineHeightControl( settings );

	// Letter Spacing
	const hasLetterSpacingControl = useHasLetterSpacingControl( settings );

	// Text Columns
	const hasTextColumnsControl = useHasTextColumnsControl( settings );

	// Text Transform
	const hasTextTransformControl = useHasTextTransformControl( settings );

	// Text Decoration
	const hasTextDecorationControl = useHasTextDecorationControl( settings );

	// Text Orientation
	const hasWritingModeControl = useHasWritingModeControl( settings );

	// Text Alignment
	const hasTextAlignmentControl = useHasTextAlignmentControl( settings );

	// White Space
	const hasWhiteSpaceControl = useHasWhiteSpaceControl( settings );

	// Word Break
	const hasWordBreakControl = useHasWordBreakControl( settings );

	const resetAll = () =>
		onChange( {
			fontFamily: undefined,
			fontSize: undefined,
			fontStyle: undefined,
			fontWeight: undefined,
			lineHeight: undefined,
			letterSpacing: undefined,
			columnCount: undefined,
			textDecoration: undefined,
			writingMode: undefined,
			textTransform: undefined,
			textAlign: undefined,
			wordBreak: undefined,
			whiteSpace: undefined,
		} );

	return (
		<ToolsPanel
			label={ _x( 'Typography', 'text', 'nelio-ab-testing' ) }
			resetAll={ resetAll }
		>
			{ hasFontFamilyEnabled && (
				<ToolsPanelItem
					label={ _x( 'Font', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.fontFamily ) }
					onSelect={ () => onChange( { fontFamily: '' } ) }
					onDeselect={ () => onChange( { fontFamily: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.fontFamily ) }
				>
					<FontFamilyControl
						fontFamilies={ fontFamilies }
						value={ getPreviewValue( props.fontFamily ) }
						onChange={ ( fontFamily ) =>
							onChange( { fontFamily } )
						}
						size="__unstable-large"
						__nextHasNoMarginBottom
						disabled={ 'readonly' === props.fontFamily?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasFontSizeEnabled && (
				<ToolsPanelItem
					label={ _x( 'Size', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.fontSize ) }
					onSelect={ () => onChange( { fontSize: '' } ) }
					onDeselect={ () => onChange( { fontSize: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.fontSize ) }
				>
					<FontSizePicker
						value={ getPreviewValue( props.fontSize ) }
						onChange={ ( fontSize ) =>
							onChange( {
								fontSize:
									'number' === typeof fontSize
										? `${ fontSize }${
												0 !== fontSize ? 'px' : ''
										  }`
										: fontSize,
							} )
						}
						fontSizes={ mergedFontSizes }
						disableCustomFontSizes={ disableCustomFontSizes }
						disabled={ 'readonly' === props.fontSize?.type }
						withReset={ false }
						withSlider
						size="__unstable-large"
					/>
				</ToolsPanelItem>
			) }
			{ hasAppearanceControl && (
				<ToolsPanelItem
					className="single-column"
					label={ appearanceControlLabel }
					hasValue={ () =>
						hasUserValue( props.fontStyle ) ||
						hasUserValue( props.fontWeight )
					}
					onSelect={ () =>
						onChange( {
							fontStyle: 'normal',
							fontWeight: '400',
						} )
					}
					onDeselect={ () =>
						onChange( {
							fontStyle: undefined,
							fontWeight: undefined,
						} )
					}
					isShownByDefault={
						hasInheritedValue( props.fontStyle ) ||
						hasInheritedValue( props.fontWeight )
					}
				>
					<FontAppearanceControl
						value={ {
							fontStyle: getPreviewValue( props.fontStyle ),
							fontWeight: getPreviewValue( props.fontWeight ),
						} }
						onChange={ onChange }
						hasFontStyles={ hasFontStyles }
						hasFontWeights={ hasFontWeights }
						fontFamilyFaces={ fontFamilyFaces }
						size="__unstable-large"
						disabled={
							'readonly' === props.fontStyle?.type ||
							'readonly' === props.fontWeight?.type
						}
					/>
				</ToolsPanelItem>
			) }
			{ hasLineHeightEnabled && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'Line height', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.lineHeight ) }
					onSelect={ () => onChange( { lineHeight: '1.2' } ) }
					onDeselect={ () => onChange( { lineHeight: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.lineHeight ) }
				>
					<LineHeightControl
						value={ getPreviewValue( props.lineHeight ) }
						onChange={ ( lineHeight ) =>
							onChange( { lineHeight } )
						}
						disabled={ 'readonly' === props.lineHeight?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasLetterSpacingControl && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'Letter spacing', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.letterSpacing ) }
					onSelect={ () => onChange( { letterSpacing: 'normal' } ) }
					onDeselect={ () =>
						onChange( { letterSpacing: undefined } )
					}
					isShownByDefault={ hasInheritedValue(
						props.letterSpacing
					) }
				>
					<LetterSpacingControl
						value={ getPreviewValue( props.letterSpacing ) }
						onChange={ ( letterSpacing ) =>
							onChange( { letterSpacing } )
						}
						size="__unstable-large"
						__unstableInputWidth="auto"
						disabled={ 'readonly' === props.letterSpacing?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasTextColumnsControl && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'Columns', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.columnCount ) }
					onSelect={ () => onChange( { columnCount: '1' } ) }
					onDeselect={ () => onChange( { columnCount: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.columnCount ) }
				>
					<NumberControl
						label={ _x( 'Columns', 'text', 'nelio-ab-testing' ) }
						max={ MAX_TEXT_COLUMNS }
						min={ MIN_TEXT_COLUMNS }
						onChange={ ( columnCount ) =>
							onChange( { columnCount } )
						}
						size="__unstable-large"
						spinControls="custom"
						value={ getPreviewValue( props.columnCount ) }
						disabled={ 'readonly' === props.columnCount?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasTextDecorationControl && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'Decoration', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.textDecoration ) }
					onSelect={ () => onChange( { textDecoration: 'none' } ) }
					onDeselect={ () =>
						onChange( { textDecoration: undefined } )
					}
					isShownByDefault={ hasInheritedValue(
						props.textDecoration
					) }
				>
					<TextDecorationControl
						value={ getPreviewValue( props.textDecoration ) }
						onChange={ ( textDecoration ) =>
							onChange( {
								textDecoration:
									'string' === typeof textDecoration
										? textDecoration
										: 'none',
							} )
						}
						disabled={ 'readonly' === props.textDecoration?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasWritingModeControl && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'Orientation', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.writingMode ) }
					onSelect={ () => onChange( { writingMode: '' } ) }
					onDeselect={ () => onChange( { writingMode: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.writingMode ) }
				>
					<WritingModeControl
						value={ getPreviewValue( props.writingMode ) }
						onChange={ ( writingMode ) =>
							onChange( {
								writingMode:
									'string' === typeof writingMode
										? writingMode
										: '',
							} )
						}
						disabled={ 'readonly' === props.writingMode?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasTextTransformControl && (
				<ToolsPanelItem
					label={ _x( 'Letter case', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.textTransform ) }
					onSelect={ () => onChange( { textTransform: 'none' } ) }
					onDeselect={ () =>
						onChange( { textTransform: undefined } )
					}
					isShownByDefault={ hasInheritedValue(
						props.textTransform
					) }
				>
					<TextTransformControl
						value={ getPreviewValue( props.textTransform ) }
						onChange={ ( textTransform ) =>
							onChange( {
								textTransform:
									'string' === typeof textTransform
										? textTransform
										: 'none',
							} )
						}
						disabled={ 'readonly' === props.textTransform?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasTextAlignmentControl && (
				<ToolsPanelItem
					label={ _x( 'Text alignment', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.textAlign ) }
					onSelect={ () => onChange( { textAlign: 'left' } ) }
					onDeselect={ () => onChange( { textAlign: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.textAlign ) }
				>
					<TextAlignmentControl
						value={ getPreviewValue( props.textAlign ) }
						onChange={ ( textAlign ) =>
							onChange( {
								textAlign:
									'string' === typeof textAlign
										? textAlign
										: 'left',
							} )
						}
						disabled={ 'readonly' === props.textAlign?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasWordBreakControl && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'Word break', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.wordBreak ) }
					onSelect={ () => onChange( { wordBreak: 'normal' } ) }
					onDeselect={ () => onChange( { wordBreak: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.wordBreak ) }
				>
					<WordBreakControl
						value={ {
							wordBreak: getPreviewValue( props.wordBreak ),
						} }
						__next40pxDefaultSize
						onChange={ onChange }
						size="__unstable-large"
						disabled={ 'readonly' === props.wordBreak?.type }
					/>
				</ToolsPanelItem>
			) }
			{ hasWhiteSpaceControl && (
				<ToolsPanelItem
					className="single-column"
					label={ _x( 'White space', 'text', 'nelio-ab-testing' ) }
					hasValue={ () => hasUserValue( props.whiteSpace ) }
					onSelect={ () => onChange( { whiteSpace: 'normal' } ) }
					onDeselect={ () => onChange( { whiteSpace: undefined } ) }
					isShownByDefault={ hasInheritedValue( props.whiteSpace ) }
				>
					<WhiteSpaceControl
						value={ {
							whiteSpace: getPreviewValue( props.whiteSpace ),
						} }
						onChange={ onChange }
						size="__unstable-large"
						disabled={ 'readonly' === props.whiteSpace?.type }
					/>
				</ToolsPanelItem>
			) }
		</ToolsPanel>
	);
}
